<template>
  <div class="job-applicants">
    <div class="d-flex align-items-baseline mb-3">
      <h5 class="font-weight-bold mb-0">Applicants</h5>
      <span v-if="total" class="applicant-total ml-2">{{ total }}</span>
    </div>

    <b-row class="applicant-filters">
      <b-col md="6" class="mb-3">
        <b-form-input
          v-model="searchText"
          type="search"
          placeholder="Search by name or trade"
          @input="onSearchInput"
          @keyup.enter="onSearchNow"
        />
      </b-col>
      <b-col md="4" class="mb-3">
        <b-form-select
          :value="status"
          :options="statusOptions"
          class="applicant-status-filter"
          @change="$emit('filter', $event)"
        />
      </b-col>
      <b-col md="2" class="mb-3">
        <b-button
          variant="outline-secondary"
          class="bg-white text-dark clear-applicant-filters"
          :disabled="!hasFilters"
          @click="onClear"
        >
          Clear
        </b-button>
      </b-col>
    </b-row>

    <p v-if="loading" class="text-muted py-3">Loading applicants...</p>
    <p v-else-if="!applications.length" class="text-muted py-3">
      {{
        hasFilters
          ? 'No applicants match these filters.'
          : 'No applicants to show.'
      }}
    </p>

    <div
      v-for="application in applications"
      :key="application.id"
      class="applicant-card mb-3"
      :class="`applicant-card--${application.status}`"
    >
      <div class="applicant-head">
        <!-- No photo or initials: companies judge on trade and message. -->
        <div class="applicant-main">
          <div class="applicant-name">{{ application.applicant.name }}</div>
          <div
            class="applicant-trade"
            :title="application.applicant.trade || ''"
          >
            {{ application.applicant.trade || 'No trade listed' }}
          </div>
          <div class="applicant-applied">
            Applied {{ formatDate(application.applied_at) }}
          </div>
        </div>
        <span
          class="applicant-status"
          :class="`applicant-status--${application.status}`"
        >
          {{ statusText(application.status) }}
        </span>
      </div>

      <!-- Plain text only — applicant messages are never rendered as HTML. -->
      <p v-if="application.message" class="applicant-message">
        {{ application.message }}
      </p>

      <!-- Required tickets the applicant holds, matched by the API (public
           files only); absent when the job asks for no tickets. -->
      <div v-if="application.ticket_match" class="applicant-tickets">
        <div class="applicant-tickets-summary">
          Tickets: {{ application.ticket_match.matched.length }} of
          {{ application.ticket_match.required.length }} required
        </div>
        <div class="applicant-ticket-list">
          <span
            v-for="ticket in application.ticket_match.matched"
            :key="`held-${ticket}`"
            class="ticket-pill ticket-pill--held"
          >
            <b-icon icon="check-circle-fill" class="mr-1" />{{ ticket }}
          </span>
          <span
            v-for="ticket in application.ticket_match.missing"
            :key="`missing-${ticket}`"
            class="ticket-pill ticket-pill--missing"
          >
            {{ ticket }}
          </span>
        </div>
      </div>

      <!-- Contact handshake: asked, then (once they agree) visible. -->
      <div
        v-if="application.contact_share_status === 'requested'"
        class="applicant-contact applicant-contact--pending"
      >
        <b-icon icon="hourglass-split" class="mr-1" />Waiting for the applicant
        to agree to share their contact details.
      </div>
      <div
        v-else-if="
          application.contact_share_status === 'shared' &&
          (application.applicant.phone || application.applicant.email)
        "
        class="applicant-contact applicant-contact--shared"
      >
        <b-icon icon="telephone-fill" class="mr-1" />
        <a
          v-if="application.applicant.phone"
          :href="`tel:${application.applicant.phone}`"
          class="mr-3"
          >{{ application.applicant.phone }}</a
        >
        <b-icon icon="envelope-fill" class="mr-1" />
        <a
          v-if="application.applicant.email"
          :href="`mailto:${application.applicant.email}`"
          >{{ application.applicant.email }}</a
        >
      </div>

      <div v-if="showFooter(application)" class="applicant-footer">
        <div class="applicant-links">
          <b-link
            v-if="canViewProfiles"
            class="applicant-link view-profile-link"
            @click.prevent="$emit('view-profile', application)"
          >
            <b-icon icon="person-lines-fill" class="mr-1" />View profile
          </b-link>
          <b-link
            v-if="application.has_resume"
            class="applicant-link view-resume-link"
            :disabled="resumeLoadingId === application.id"
            @click.prevent="$emit('view-resume', application)"
          >
            <b-icon icon="file-earmark-text" class="mr-1" />View resume
          </b-link>
          <b-link
            class="applicant-link notes-link"
            @click.prevent="toggleNotes(application)"
          >
            <b-icon icon="journal-text" class="mr-1" />{{
              notesOpenId === application.id ? 'Hide notes' : 'Notes'
            }}
          </b-link>
        </div>

        <div class="applicant-actions">
          <template v-if="showTalentActions(application)">
            <favourite-toggle
              :tradie-id="application.applicant.id"
              class="applicant-favourite"
            />
            <add-to-pool-dropdown
              :tradie-id="application.applicant.id"
              class="applicant-pool"
            />
          </template>
          <workforce-invite-button
            v-if="canViewProfiles"
            :workforce-invitation-status="
              application.workforce_invitation_status
            "
            :is-in-workforce="!!application.is_in_workforce"
            has-applied
            @invite="$emit('invite', application)"
          />
          <b-button
            v-if="
              canManage &&
              application.status !== 'rejected' &&
              application.contact_share_status !== 'shared'
            "
            size="sm"
            variant="outline-secondary"
            class="reject-btn"
            :disabled="updatingId === application.id"
            @click="askReject(application)"
          >
            Reject
          </b-button>
          <b-button
            v-if="canManage && application.status === 'rejected'"
            size="sm"
            variant="outline-primary"
            class="reinstate-btn"
            :disabled="updatingId === application.id"
            @click="$emit('reinstate', application)"
          >
            Reinstate
          </b-button>
          <!-- Once they have shared their details the process moves on. -->
          <template
            v-if="canManage && application.contact_share_status === 'shared'"
          >
            <b-dropdown
              size="sm"
              right
              :variant="
                stages.includes(application.status)
                  ? 'primary'
                  : 'outline-primary'
              "
              :text="stageMenuText(application)"
              class="stage-dropdown"
              :disabled="updatingId === application.id"
            >
              <b-dropdown-item
                v-for="stage in stages"
                :key="stage"
                :class="`stage-btn stage-btn--${stage}`"
                :active="application.status === stage"
                :disabled="application.status === stage"
                @click="$emit('update-status', application, stage)"
              >
                {{ statusText(stage) }}
              </b-dropdown-item>
              <!-- They can go through the stages and still not be hired. -->
              <b-dropdown-divider />
              <b-dropdown-item
                class="stage-btn stage-btn--reject"
                :disabled="application.status === 'rejected'"
                @click="askReject(application)"
              >
                Reject
              </b-dropdown-item>
            </b-dropdown>
          </template>
          <template v-else>
            <b-button
              v-if="
                canManage &&
                application.status !== 'rejected' &&
                application.contact_share_status !== 'requested'
              "
              size="sm"
              variant="primary"
              class="shortlist-contact-btn"
              :disabled="updatingId === application.id"
              @click="$emit('update-status', application, 'shortlisted', true)"
            >
              Shortlist &amp; Contact
            </b-button>
            <b-button
              v-if="canManage && application.status === 'new'"
              size="sm"
              variant="outline-primary"
              class="shortlist-btn"
              :disabled="updatingId === application.id"
              @click="$emit('update-status', application, 'shortlisted')"
            >
              Shortlist only
            </b-button>
          </template>
        </div>
      </div>

      <applicant-notes
        v-if="notesOpenId === application.id"
        :job-id="application.job_posting_id"
        :application-id="application.id"
        :can-manage="canManage"
      />
    </div>

    <b-pagination
      v-if="total > perPage"
      :value="page"
      :total-rows="total"
      :per-page="perPage"
      align="center"
      class="mt-3"
      @change="$emit('page-change', $event)"
    />

    <confirmation-modal
      :show-modal="!!rejectTarget"
      title="Reject applicant"
      :message="rejectMessage"
      ok-label="REJECT APPLICANT"
      ok-variant="danger"
      @close="rejectTarget = null"
      @ok="onRejectConfirm"
    >
      <div class="reject-option">
        <b-form-checkbox v-model="alsoNotSuited" class="also-not-suited">
          Do not display this job posting to this applicant
        </b-form-checkbox>
      </div>
    </confirmation-modal>
  </div>
</template>

<script>
import WorkforceInviteButton from '@/components/candidates/WorkforceInviteButton.vue'
import { mapActions } from 'vuex'
import AddToPoolDropdown from '@/components/candidates/AddToPoolDropdown.vue'
import ApplicantNotes from '@/components/jobPostings/ApplicantNotes.vue'
import FavouriteToggle from '@/components/candidates/FavouriteToggle.vue'
import ConfirmationModal from '@/components/modals/ConfirmationModal.vue'
import {
  APPLICATION_STATUS_LABELS,
  CONTACT_STAGES,
  formatDate,
} from '@/utils/jobPostings'

// Applicants for one job. Presentational: the parent owns fetching and the
// status update call (gated on jobs.edit via `canManage`).
export default {
  name: 'JobApplicants',

  components: {
    AddToPoolDropdown,
    ApplicantNotes,
    ConfirmationModal,
    FavouriteToggle,
    WorkforceInviteButton,
  },

  props: {
    applications: {
      type: Array,
      default: () => [],
    },
    loading: Boolean,
    canManage: Boolean,
    jobClosed: Boolean,
    // Can open an applicant's candidate profile and invite them to the
    // workforce (Open to Work on, and the candidates permissions). The parent
    // decides; the invite button still checks add_to_workforce itself.
    canViewProfiles: Boolean,
    // Active status filter ('' = the API default, which hides withdrawn).
    status: {
      type: String,
      default: '',
    },
    updatingId: {
      type: [Number, String],
      default: null,
    },
    // Application whose resume link is being fetched (disables its link).
    resumeLoadingId: {
      type: [Number, String],
      default: null,
    },
    // Applied search term, so the box starts with what the list is showing.
    search: {
      type: String,
      default: '',
    },
    // The API paginates: `total` is the count across all pages.
    page: {
      type: Number,
      default: 1,
    },
    perPage: {
      type: Number,
      default: 20,
    },
    total: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      searchText: this.search,
      searchTimer: null,
      stages: CONTACT_STAGES,
      rejectTarget: null,
      notesOpenId: null,
      alsoNotSuited: false,
      statusOptions: [
        { value: '', text: 'All applicants' },
        { value: 'new', text: 'New' },
        { value: 'shortlisted', text: 'Shortlisted' },
        { value: 'interview', text: 'Interview' },
        { value: 'offer', text: 'Offer' },
        { value: 'hired', text: 'Hired' },
        { value: 'rejected', text: 'Rejected' },
        { value: 'withdrawn', text: 'Withdrawn' },
      ],
    }
  },

  computed: {
    rejectMessage() {
      const name = this.rejectTarget && this.rejectTarget.applicant.name
      return `The application from ${
        name || 'this applicant'
      } will be marked as unsuccessful.`
    },

    hasFilters() {
      return !!(this.searchText.trim() || this.status)
    },
  },

  watch: {
    jobClosed: {
      immediate: true,
      handler(closed) {
        if (closed && this.canViewProfiles)
          this.fetchFavourites().catch(() => {})
      },
    },
  },

  beforeDestroy() {
    clearTimeout(this.searchTimer)
  },

  methods: {
    ...mapActions('candidate', ['fetchFavourites']),

    formatDate,

    showTalentActions(application) {
      return (
        this.jobClosed &&
        this.canViewProfiles &&
        !!(application.applicant && application.applicant.id)
      )
    },

    askReject(application) {
      this.alsoNotSuited = false
      this.rejectTarget = application
    },

    toggleNotes(application) {
      this.notesOpenId =
        this.notesOpenId === application.id ? null : application.id
    },

    onRejectConfirm() {
      const application = this.rejectTarget
      if (!application) return
      this.rejectTarget = null
      this.$emit('reject', application, this.alsoNotSuited)
    },

    // The menu shows where they are in the hiring stages, or "Move to stage".
    stageMenuText(application) {
      return this.stages.includes(application.status)
        ? this.statusText(application.status)
        : 'Move to stage'
    },

    // Wait for a pause in typing so each keystroke is not a request. Emptying
    // the box is a deliberate "show everyone", so that goes straight through.
    onSearchInput() {
      clearTimeout(this.searchTimer)
      if (!this.searchText.trim()) return this.emitSearch()
      this.searchTimer = setTimeout(this.emitSearch, 500)
    },

    // Enter skips the wait.
    onSearchNow() {
      clearTimeout(this.searchTimer)
      this.emitSearch()
    },

    emitSearch() {
      this.$emit('search', this.searchText.trim())
    },

    onClear() {
      clearTimeout(this.searchTimer)
      this.searchText = ''
      this.$emit('clear')
    },

    statusText(status) {
      return APPLICATION_STATUS_LABELS[status] || status
    },

    showFooter(application) {
      return application.status !== 'withdrawn'
    },
  },
}
</script>

<style lang="scss" scoped>
// Gray values follow the newer Tailwind-style scale in DESIGN.md.
.applicant-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  // A thin status-coloured edge is the only colour on the card itself.
  border-left: 4px solid #d1d5db;
  border-radius: 12px;
  padding: 1.1rem 1.25rem;
  // Resting card shadow from DESIGN.md.
  box-shadow: 0px 10px 25px #00429d0d;

  &--new {
    border-left-color: #006cff;
  }

  &--shortlisted,
  &--interview,
  &--offer,
  &--hired {
    border-left-color: #1e9e63;
  }

  &--rejected,
  &--withdrawn {
    border-left-color: #d1d5db;
  }

  &--withdrawn {
    opacity: 0.7;
  }
}

.applicant-head {
  display: flex;
  align-items: flex-start;
  gap: 0.9rem;
}

.applicant-main {
  flex: 1;
  min-width: 0;
}

.applicant-name {
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.3;
  color: #111827;
}

// A long list of tickets/trades must not push the status pill off the card.
.applicant-trade {
  margin-top: 0.1rem;
  font-size: 0.875rem;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.applicant-applied {
  margin-top: 0.1rem;
  font-size: 0.75rem;
  color: #9ca3af;
}

// A plain span, not b-badge: the global .badge sizing made the pill huge.
.applicant-status {
  flex-shrink: 0;
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.4;
  background-color: #f3f4f6;
  color: #6b7280;

  &--new {
    background-color: #e6f0ff;
    color: #006cff;
  }

  &--shortlisted,
  &--interview,
  &--offer,
  &--hired {
    background-color: #e3f2e9;
    color: #1e9e63;
  }

  &--withdrawn {
    background-color: #f9fafb;
    color: #9ca3af;
  }
}

.applicant-contact {
  margin-top: 0.75rem;
  font-size: 0.875rem;

  &--pending {
    color: #6b7280;
  }

  &--shared {
    color: #111827;
    font-weight: 600;
  }
}

.applicant-tickets {
  margin-top: 0.9rem;
}

.applicant-tickets-summary {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 0.35rem;
}

.applicant-ticket-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.ticket-pill {
  font-size: 0.8rem;
  padding: 0.2rem 0.65rem;
  border-radius: 999px;

  &--held {
    background-color: #e6f0ff;
    color: #006cff;
    font-weight: 600;
  }

  &--missing {
    border: 1px dashed #d1d5db;
    color: #6b7280;
  }
}

.applicant-message {
  margin: 0.9rem 0 0;
  padding: 0.65rem 0.9rem;
  background-color: #f9fafb;
  border-radius: 8px;
  color: #374151;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.applicant-footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 0.9rem;
  padding-top: 0.9rem;
  border-top: 1px solid #f3f4f6;
}

.applicant-links {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 1.25rem;
}

.applicant-link {
  font-size: 0.875rem;
  font-weight: 600;
}

.applicant-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.applicant-total {
  font-size: 0.875rem;
  font-weight: 700;
  color: #6b7280;
}

// Match the 50px search and select beside it (the global .btn is 56px).
.clear-applicant-filters {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 50px;
  padding: 0 16px;
  font-weight: 600;
}

// The tick box sits in a soft panel so it reads as an option, with room
// before the buttons.
.reject-option {
  background: #f9fafb;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 16px;
  color: #374151;
}
</style>
